// modules/settings/index.ts — Platik-IA
import { bus }      from '../../core/event-bus';
import { getModule } from '../../core/registry';
import { supabase } from '../../services/supabase';
import { getSetting, setSetting } from '../../services/idb';
import { env }      from '../../core/env';
import type { SettingsAPI } from './contracts';

export async function initSettings(): Promise<SettingsAPI> {
  bus.emit('settings:ready');

  return {
    get<T>(key: string, fallback: T): T {
      // Sync read desde caché en memoria o localStorage
      try {
        const raw = localStorage.getItem(`platik:${key}`);
        return raw !== null ? (JSON.parse(raw) as T) : fallback;
      } catch { return fallback; }
    },

    async set(key, value) {
      localStorage.setItem(`platik:${key}`, JSON.stringify(value));
      await setSetting(key, value);
      bus.emit('settings:updated', { key, value });
    },

    async saveGeminiKey(key: string) {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No hay sesión activa');

      const res = await fetch(`${env.VITE_SUPABASE_URL}/functions/v1/key-manager`, {
        method:  'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type':  'application/json',
        },
        body: JSON.stringify({ action: 'save', key }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Error desconocido' }));
        throw new Error(err.error ?? `Error ${res.status}`);
      }

      bus.emit('settings:updated', { key: 'hasGeminiKey', value: true });
      bus.emit('notifications:show', { message: 'API key guardada correctamente ✓', type: 'success' });
    },

    async deleteGeminiKey() {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      await fetch(`${env.VITE_SUPABASE_URL}/functions/v1/key-manager`, {
        method:  'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type':  'application/json',
        },
        body: JSON.stringify({ action: 'delete' }),
      });

      bus.emit('settings:updated', { key: 'hasGeminiKey', value: false });
    },

    async saveSheetId(id: string) {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      await supabase.from('users').update({ sheet_id: id }).eq('id', session.user.id);
      await setSetting('sheetId', id);
      getModule<{ setSheetId: (id: string) => void }>('auth')?.setSheetId(id);
      bus.emit('settings:updated', { key: 'sheetId', value: id });
      bus.emit('notifications:show', { message: 'Hoja de cálculo vinculada ✓', type: 'success' });
    },

    dispose() {},
  };
}
